import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import classNames from "classnames";
import { memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { isServerSide } from "app/utils/utils";
import {
	defaultValues,
	validateSearch,
} from "app/pages/SmartDP/Search/SDPSearchForm/smartDPSearchFormSchema";
import {
	DESTINATION_USA_CODE,
	FLAGSHIP_EVENT_KPIS,
	FS_QUOTE_EVENT_NAME,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import { FormattedMessage, useIntl } from "react-intl";
import { Form, Formik } from "formik";
import CalendarDisabledView from "app/pages/SmartDP/Search/SDPSearchForm/CalendarDisabledView";
import { differenceInDays } from "date-fns";
import TravellersRoomDrawerButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomDrawerButton";
import TravellersRoomButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomButton";
import { Button } from "app/components/ui/Button/Button";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import PropTypes from "prop-types";
import HotelOnlyDateCalendarContainer from "app/pages/HotelOnly/HotelOnlySearchForm/HotelOnlyDateCalendarContainer";
import "app/pages/.shared/form/SearchForm.scss";
import "./HotelOnlySearchForm.scss";
import LastSearchContainer from "app/features/SearchEngine/LastSearch/LastSearchContainer";
import { SearchJourney } from "app/features/SearchEngine/searchJourney/SearchJourney";
import FloatingDestinationCombobox from "app/pages/.shared/form/FloatingAdvancedSelectFormik/FloatingDestinationCombobox";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { renderHotelOnlyDestinationOption } from "app/features/SearchEngine/searchJourney/components/locationCombobox/LocationCombobox";
import { DropdownField } from "app/features/SearchEngine/searchJourney/components/dropdownField/DropdownField";
import TravellersRoomForm from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomForm";

const HOTEL_ONLY_SEARCH_HOTELS_PATH = "/search-hotels/hotelonly";

const HotelOnlySearchForm = ({
	onSuccess = () => {},
	destinations = [],
	initialValues = {},
	topDestinationsList = [],
	validateOnMount,
	usePortalForMenu,
	displayLoader,
	errorScrollIntoView = true,
	onFieldFocus = () => {},
	isAutoScrollActive,
	resetAllSDPProductsFilter = () => {},
	isCompact = false,
	isLoading = false,
	submitButtonLabel,
}) => {
	const { sendHits } = useFlagship();
	const { track } = useAnalytics();
	const { formatMessage } = useIntl();

	const selectRefs = {
		destinationResort: useRef(null),
		departureDate: useRef(null),
	};
	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const [isTravellersOpen, setIsTravellersOpen] = useState(false);

	const handleSubmit = useCallback(
		(values, actions) => {
			resetAllSDPProductsFilter();
			actions.setSubmitting(false);
			onSuccess({ values, pathname });
		},
		[destinations, pathname]
	);

	const getLocationSelectionEventName = useCallback(
		(type, isTopSelection) => `form_click_${type}_${isTopSelection ? "popular" : "manual"}`,
		[]
	);

	// fix pour ne pas avoir les labels sur 2 lignes au premier rendu
	const mergeInitialValues = isServerSide
		? {
				occupancies: [{ adults: 2, children: 0, childrenBirthdates: [] }],
			}
		: {
				...defaultValues,
				...initialValues,
			};

	const isListingOrQuote =
		pathname.includes("/hotelonly/listing") || pathname === "/hotelonly/booking/quote";

	const onHandleClickSearchButton = useCallback(() => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FS_QUOTE_EVENT_NAME.CLICK_ON_SEARCH_CTA,
		});
	}, []);

	const [refs, setRefs] = useState(null);

	const updateFloatingPositionReference = useCallback(refs => {
		setRefs(refs);
	}, []);
	const customFieldIds = ["departureDate"];

	const fieldIds = useMemo(() => ["destinationResort", "departureDate"], []);

	const fieldOrder = useMemo(
		() => fieldIds.map(id => ({ id, ref: selectRefs[id] })),
		[fieldIds, selectRefs]
	);
	const getFieldValue = (values, fieldId) => {
		const nestedFields = {
			departureDate: ["travelDates", "departureDate"],
		};

		if (nestedFields[fieldId]) {
			return nestedFields[fieldId].reduce(
				(acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
				values
			);
		}

		return values[fieldId];
	};

	const handleFieldChange = useCallback(
		(fieldId, setFieldValue, value, values) => {
			setFieldValue(fieldId, value);

			if (value) {
				const currentIndex = fieldOrder.findIndex(field => field.id === fieldId);
				if (currentIndex === -1) {
					return;
				}

				const previousFields = fieldOrder.slice(0, currentIndex);
				const firstEmptyPrevField = previousFields.find(
					field => !getFieldValue(values, field.id)
				);

				if (firstEmptyPrevField && firstEmptyPrevField.ref.current) {
					if (customFieldIds.includes(firstEmptyPrevField.id)) {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.click();
						});
					} else {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.focus();
						});
					}
					return;
				}

				let nextIndex = currentIndex + 1;

				while (nextIndex < fieldOrder.length) {
					const nextField = fieldOrder[nextIndex];
					if (!getFieldValue(values, nextField.id) && nextField.ref.current) {
						requestAnimationFrame(() => {
							nextField.ref.current.click();
						});
						break;
					}
					nextIndex++;
				}
			}
		},
		[fieldOrder, customFieldIds]
	);

	const SEARCH_STEPS = useMemo(
		() => [
			{
				id: "destinationResort",
				title: formatMessage({ id: "sdp.search.destination.hotel.input.label" }),
			},
			{
				id: "travelDates",
				title: formatMessage({
					id: "sdp.search.departure.date.input.label",
				}),
			},
		],
		[formatMessage]
	);

	return (
		<div className="search-form search-form--hotel-only" ref={refs?.setPositionReference}>
			<Formik
				enableReinitialize
				initialValues={mergeInitialValues}
				validate={validateSearch(true)}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={handleSubmit}
				initialTouched={
					validateOnMount && {
						destinationResort: true,
						travelDates: {
							departureDate: true,
							endDate: true,
						},
						duration: {
							code: true,
						},
						occupancies: true,
					}
				}
				validateOnMount={validateOnMount}
			>
				{({ values, errors, isValid, isSubmitting, setFieldValue, submitCount }) => {
					const handleTravellersConfirmation = values => {
						setFieldValue("occupancies", values);
					};

					const destinationResortId = values?.destinationResort?.isHotel
						? values?.destinationResort?.places?.[0]?.id
						: values?.destinationResort?.code;
					const isUsaDestination = DESTINATION_USA_CODE.includes(destinationResortId);

					const travelerCount = (values.occupancies || []).reduce(
						(total, occupancy) => total + occupancy.adults + occupancy.children,
						0
					);
					const roomCount = (values.occupancies || []).length;
					const travellersValueLabel = `${formatMessage(
						{ id: "traveler.count" },
						{ count: travelerCount }
					)}, ${formatMessage(
						{
							id:
								roomCount === 1
									? "general.room.count.label.singular"
									: "general.room.count.label.plural",
						},
						{ count: roomCount }
					)}`;

					const renderDestinationField = fieldClassName => (
						<FloatingDestinationCombobox
							id="destinationResort"
							name="destinationResort"
							usePortalForMenu={usePortalForMenu}
							selectRef={selectRefs.destinationResort}
							hotelSearchPath={HOTEL_ONLY_SEARCH_HOTELS_PATH}
							renderDestinationOption={renderHotelOnlyDestinationOption}
							onChange={(value, meta) => {
								handleFieldChange(
									"destinationResort",
									setFieldValue,
									value,
									values
								);

								if (value) {
									if (value?.isHotel) {
										track(
											getLocationSelectionEventName(
												"hotel",
												meta?.showChipMenu
											),
											{
												hotelName: value?.label,
												hotelId: value?.code,
												destinationPlaces: value?.places,
											}
										);
									} else {
										track(
											getLocationSelectionEventName(
												"destination",
												meta?.showChipMenu
											),
											{
												locationLabel:
													value?.label ||
													value?.labels?.slice(-1).join(", "),
											}
										);
									}
								}

								if (value?.label) {
									sendHits({
										type: HitType.EVENT,
										category: EventCategory.ACTION_TRACKING,
										action: FLAGSHIP_EVENT_KPIS.FLAG_SDP_FUNNEL_FLAGS_TRIGGERED,
										label: value?.label,
									});
								}
							}}
							onFocus={onFieldFocus}
							className={fieldClassName}
							data-testid="destination-resort-input"
							label={
								<FormattedMessage id="sdp.search.destination.hotel.input.label" />
							}
							getOptionLabel={({ label }) => label}
							options={destinations}
							optionsWhenEmpty={topDestinationsList}
							value={values?.destinationResort || ""}
							loading={displayLoader}
							floatingConfig={{
								placement: isCompact ? "bottom" : "bottom-start",
								offset: ({ rects }) => ({
									mainAxis: -rects.reference.height - 16,
									crossAxis: isCompact ? 0 : -16,
								}),
							}}
						/>
					);

					const renderDateField = fieldClassName => (
						<HotelOnlyDateCalendarContainer
							id="travelDates"
							name="travelDates"
							data-testid="travel-date-input"
							selectRef={selectRefs.departureDate}
							onFocus={onFieldFocus}
							isAutoScrollActive={isAutoScrollActive}
							className={fieldClassName}
							isCompact={isCompact}
							popperOffset={isCompact ? [0, 28] : [0, -58]}
							updateFloatingPositionReference={
								isCompact ? () => {} : updateFloatingPositionReference
							}
							calendarPopoverWidth={1024}
							departureLabel={
								<FormattedMessage id="hotel.only.search.departure.date.side.panel.title" />
							}
							returnLabel={
								<FormattedMessage id="hotel.only.search.destination.date.side.panel.title" />
							}
							calendarDisabledView={
								<CalendarDisabledView
									mainMessage={
										values.departureCity && values.destinationResort ? (
											<FormattedMessage id="hotel.only.calendar.unavailable.date.label" />
										) : (
											<FormattedMessage id="hotel.only.calendar.no.destination.no.departure.city.label" />
										)
									}
									secondaryMessage={
										values.departureCity && values.destinationResort ? (
											<FormattedMessage id="sdp.calendar.unavailable.date.sublabel" />
										) : null
									}
								/>
							}
							handleDayChange={({ selectedEndDate, selectedDepartureDate }) => {
								if (selectedEndDate && selectedDepartureDate) {
									const daysDifference = differenceInDays(
										new Date(selectedEndDate),
										new Date(selectedDepartureDate)
									);
									setFieldValue("duration", {
										code: daysDifference,
									});
								} else {
									setFieldValue("duration", {
										code: "",
									});
								}
							}}
							loading={displayLoader}
						/>
					);

					const renderCompactFields = () => (
						<>
							{renderDestinationField("sdp-search-form__destination")}
							{renderDateField("sdp-search-form__travel-dates")}
							<DropdownField
								className="sdp-search-form__travellers"
								id="travellersRooms"
								label={formatMessage({
									id: "sdp.search.travellers.input.label",
								})}
								value={travellersValueLabel}
								error={Boolean(errors?.occupancies)}
								open={isTravellersOpen}
								onOpenChange={setIsTravellersOpen}
								side="bottom"
								avoidCollisions={false}
							>
								<TravellersRoomForm
									initialValues={values.occupancies}
									handleValidation={formValues => {
										handleTravellersConfirmation(formValues.occupancies);
										setIsTravellersOpen(false);
									}}
									validateOnMount={validateOnMount}
									warningMessage={isUsaDestination}
								/>
							</DropdownField>
						</>
					);

					return (
						<Form
							className={classNames("search-form__form search-form--hotel-only", {
								"search-form--hotel-only--compact": isCompact,
							})}
							data-testid="sdp-search-form"
						>
							{isCompact ? (
								renderCompactFields()
							) : (
								<>
									{isMobile && (
										<SearchJourney
											travelMode="hotelonly"
											steps={SEARCH_STEPS}
										/>
									)}

									{!isMobile && (
										<>
											{renderDestinationField(
												"search-form__field search-form__field-destination"
											)}
											{renderDateField("search-form__field")}
										</>
									)}
									{isMobile ? (
										<TravellersRoomDrawerButton
											className="search-form__field search-form__field-rooms search-form__field--fullsize search-form__field-travellers-hotel-only"
											id="travellersRooms"
											onConfirmation={handleTravellersConfirmation}
											initialValues={values.occupancies}
											label={
												<FormattedMessage id="sdp.search.travellers.input.label" />
											}
											validateOnMount={validateOnMount}
											warningMessage={isUsaDestination}
										/>
									) : (
										<TravellersRoomButton
											className="search-form__field search-form__field-rooms search-form__field--fullsize"
											id="travellersRooms"
											onFocus={onFieldFocus}
											isAutoScrollActive={isAutoScrollActive}
											onConfirmation={handleTravellersConfirmation}
											initialValues={values.occupancies}
											label={
												<FormattedMessage id="sdp.search.travellers.input.label" />
											}
											stickyMode={false}
											validateOnMount={validateOnMount}
											loading={displayLoader}
											popperWidth={360}
											popperOffset={[80, 4]}
											warningMessage={isUsaDestination}
										/>
									)}
								</>
							)}

							{/* Do not display error message on validationOnMount (redirection from emirates) */}
							{!isValid && submitCount >= 1 && (
								<div className="search-form__error">
									<FormErrorMessages
										errors={errors}
										isSubmitting={isSubmitting}
										submitCount={submitCount}
										disableErrorsDetails={true}
										message={
											<FormattedMessage id="sdp.search.form.error.message" />
										}
										scrollInToView={errorScrollIntoView}
									/>
								</div>
							)}
							<div className="search-form__footer">
								<Button
									variant="primary"
									size="lg"
									type="submit"
									className="search-form__button"
									loading={isSubmitting || isLoading}
									data-testid="search-form-button"
									onClick={onHandleClickSearchButton}
									disabled={displayLoader || isSubmitting}
								>
									{submitButtonLabel ?? (
										<FormattedMessage
											id={
												isListingOrQuote
													? "sdp.search.refresh.label"
													: "sdp.search.cta.label"
											}
										/>
									)}
								</Button>

								<div
									className={classNames("search-form__last-search", {
										"search-form__last-search--compact": isCompact,
									})}
								>
									<LastSearchContainer
										websiteType={WEBSITE_TUNNEL_TYPES.HOTEL_ONLY}
										updateFloatingPositionReference={
											updateFloatingPositionReference
										}
										onFocus={onFieldFocus}
										isCompact={isCompact}
									/>
								</div>
							</div>
							{!isCompact && <div className="search-form__first-line-break" />}
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

HotelOnlySearchForm.propTypes = {
	onSuccess: PropTypes.func,
	destinations: PropTypes.array,
	initialValues: PropTypes.object,
	topDestinationsList: PropTypes.array,
	usePortalForMenu: PropTypes.bool,
	onFieldFocus: PropTypes.func,
	validateOnMount: PropTypes.bool,
	displayLoader: PropTypes.bool,
	errorScrollIntoView: PropTypes.bool,
	isAutoScrollActive: PropTypes.bool,
	resetAllSDPProductsFilter: PropTypes.func,
	isCompact: PropTypes.bool,
	isLoading: PropTypes.bool,
	submitButtonLabel: PropTypes.node,
};

export default memo(HotelOnlySearchForm);
